// Conversation settings for one NPC: on/off, who it is, a greeting, and its
// voice. Used in the NPC creator (Advanced tab) and the NPC's builder panel.
// Replies are paid by the world owner in talk credits, shown here with a way
// to buy more.
import React, { useEffect, useState } from 'react';
import { PERSONA_MAX, GREETING_MAX, type NpcTalk } from '../../config/npc';
import { NPC_TALK_UNITS, voicesFor, defaultVoice, type NpcVoiceTier } from '../../config/npcVoices';
import { fetchTalkCredits, type TalkCredits } from '../../services/npcTalk';
import { CheckoutModal } from '../checkout/CheckoutModal';
import { paymentsEnabled } from '../../config/payments';
import './NpcTalk.css';

export const newTalk = (gender: string): NpcTalk => ({
  enabled: true, persona: '', greeting: '', tier: 'standard', voice: defaultVoice('standard', gender),
});

interface Props {
  talk: NpcTalk | undefined;
  gender: string;
  name: string;
  disabled?: boolean;
  onChange: (talk: NpcTalk) => void;
}

export const NpcTalkSettings: React.FC<Props> = ({ talk, gender, name, disabled, onChange }) => {
  const [credits, setCredits] = useState<TalkCredits | null>(null);
  const [buying, setBuying] = useState(false);
  useEffect(() => { fetchTalkCredits().then(setCredits); }, []);

  const t = talk ?? { ...newTalk(gender), enabled: false };
  const set = (patch: Partial<NpcTalk>) => onChange({ ...t, ...patch });
  const setTier = (tier: NpcVoiceTier) => set({ tier, voice: defaultVoice(tier, gender) });
  const voices = voicesFor(t.tier);
  const out = credits && !credits.admin && (credits.remaining ?? 0) < NPC_TALK_UNITS[t.tier];

  return (
    <div className="npc-talk">
      <label className="npc-talk-toggle">
        <input type="checkbox" checked={t.enabled} disabled={disabled} onChange={(e) => (e.target.checked && !talk ? onChange(newTalk(gender)) : set({ enabled: e.target.checked }))} />
        <span><b>Talks with visitors</b><small>Visitors walk up, type or hold to talk, and {name || 'the NPC'} answers out loud.</small></span>
      </label>

      {t.enabled && (
        <>
          <div className="npc-talk-field">
            <span>Who is {name || 'this NPC'}? <em>{t.persona.length}/{PERSONA_MAX}</em></span>
            <textarea
              rows={4} maxLength={PERSONA_MAX} disabled={disabled} value={t.persona}
              placeholder={`e.g. ${name || 'Zora'} runs the bar here. Loves 80s music, teases regulars, and knows the DJ plays at 9.`}
              onChange={(e) => set({ persona: e.target.value })}
            />
          </div>
          <div className="npc-talk-field">
            <span>Greeting <em>(optional, said when someone starts talking)</em></span>
            <input type="text" maxLength={GREETING_MAX} disabled={disabled} value={t.greeting || ''} placeholder="Hey you! First time here?" onChange={(e) => set({ greeting: e.target.value })} />
          </div>
          <div className="npc-talk-field">
            <span>Voice</span>
            <div className="npc-talk-tiers">
              {(['standard', 'premium'] as NpcVoiceTier[]).map((tier) => (
                <button key={tier} type="button" disabled={disabled} className={t.tier === tier ? 'on' : ''} onClick={() => setTier(tier)}>
                  <b>{tier === 'standard' ? 'Standard' : 'Premium'}</b>
                  <small>{NPC_TALK_UNITS[tier]} credit{NPC_TALK_UNITS[tier] > 1 ? 's' : ''} per reply</small>
                </button>
              ))}
            </div>
            <select value={t.voice} disabled={disabled} onChange={(e) => set({ voice: e.target.value })}>
              {(['Female', 'Male'] as const).map((g) => (
                <optgroup key={g} label={g}>
                  {voices.filter((v) => v.gender === g).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <div className={`npc-talk-credits ${out ? 'out' : ''}`}>
            <span>
              {!credits ? 'Talk credits…'
                : credits.admin ? 'Talk credits: unlimited (admin)'
                : out ? `Out of talk credits. ${name || 'Your NPC'} will rest instead of talking.`
                : `${credits.remaining?.toLocaleString()} talk credits left`}
              <small>Your credits pay for every reply in your worlds. Visitors never pay.</small>
            </span>
            {credits && !credits.admin && paymentsEnabled && (
              <button type="button" onClick={() => setBuying(true)}>Get {credits.packUnits} for $4.99</button>
            )}
          </div>
        </>
      )}

      {buying && (
        <CheckoutModal
          product="npc_talk"
          returnPath={window.location.pathname}
          onClose={() => setBuying(false)}
          onPaid={() => { setBuying(false); fetchTalkCredits().then(setCredits); }}
        />
      )}
    </div>
  );
};

export default NpcTalkSettings;
